import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";

import type { SimpleProjectDto, SystemGroupDto } from "@/api/types";
import { Button } from "@/components/Button/Button";
import { Drawer } from "@/components/Drawer/Drawer";
import { Form } from "@/components/Form/Form";
import { FormErrorWrapper } from "@/components/Form/FormErrorWrapper";
import { FormField } from "@/components/Form/FormField";
import { FormMultiSelect } from "@/components/Form/FormMultiSelect";
import { FormSelect } from "@/components/Form/FormSelect";
import { Building04Icon } from "@/components/Icons/Building04Icon";
import MultiLineContent from "@/components/MultiLineContent/MultiLineContent";
import { sortAlphabetically } from "@/helpers/util";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { GroupIcon } from "@/modules/community-groups/components/GroupIcons";
import type { AdminCreatePostFormAudienceFieldItem } from "@/modules/messages/pages/AdminCreatePost/components/AdminCreatePostFormAudienceSelector";
import { AdminCreatePostFormAudienceSelectorButtonItem } from "@/modules/messages/pages/AdminCreatePost/components/AdminCreatePostFormAudienceSelector";
import { useCommunityFeedQueries } from "@/queries/communityFeed";
import { useTranslation } from "@/translations";

import type { AdminCreateMassPostFormValues } from "..";

type CreateMassPostFormAudienceSelectorFormValues = {
  selectedGroup: SystemGroupDto;
  selectedProjects: SimpleProjectDto[];
};

type CreateMassPostFormAudienceSelectorProps = {
  selectedGroup: SystemGroupDto | undefined;
  selectedProjects: SimpleProjectDto[];
  selectedPostType: AdminCreateMassPostFormValues["postType"];
  isError: boolean;
  onConfirmSelection: ({ group, projects }: { group: SystemGroupDto; projects: SimpleProjectDto[] }) => void;
};

export function CreateMassPostFormAudienceSelector({
  selectedGroup,
  selectedProjects,
  selectedPostType,
  isError,
  onConfirmSelection,
}: CreateMassPostFormAudienceSelectorProps): React.ReactNode {
  const [isDrawerOpened, drawerHandler] = useBool(false);

  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const form = useForm<CreateMassPostFormAudienceSelectorFormValues>({
    defaultValues: {
      selectedGroup: selectedGroup,
      selectedProjects: selectedProjects,
    },
    reValidateMode: "onChange",
    mode: "onChange",
  });
  const currGroup = useWatch({
    name: "selectedGroup",
    control: form.control,
  });
  const currProjects = useWatch({
    name: "selectedProjects",
    control: form.control,
  });

  const communityFeedQueries = useCommunityFeedQueries();
  const { data: audienceGroups = [] } = useQuery(
    communityFeedQueries.listMassMessageAudienceGroups({
      MessageType: selectedPostType,
      Sorting: "name",
    }),
  );
  const { data: audienceProjects = [], isFetching: isFetchingAudienceProjects } = useQuery({
    ...communityFeedQueries.listMassMessageAudienceProjects({
      MessageType: selectedPostType,
      GroupId: currGroup?.id,
    }),
  });

  useEffect(() => {
    if (selectedGroup) {
      form.setValue("selectedGroup", selectedGroup);
    }

    form.setValue("selectedProjects", selectedProjects);
  }, [form, selectedGroup, selectedProjects]);

  const handleSubmit = async () => {
    // A manual form validation is needed here
    // because both forms in the page wil be submitted if a button of `type="submit"` is used
    const isFormValid = await form.trigger();

    if (isFormValid) {
      const formValues = form.getValues();

      onConfirmSelection({
        group: formValues.selectedGroup,
        projects: formValues.selectedProjects,
      });

      drawerHandler.setFalse();
    }
  };

  const selectedAudienceItems = useMemo(() => {
    const items: AdminCreatePostFormAudienceFieldItem[] = [];

    if (!selectedGroup && selectedProjects.length === 0) {
      items.push({
        value: "[...]",
      });
    } else {
      if (selectedGroup) {
        items.push({
          icon: <GroupIcon size={16} icon={selectedGroup.icon} />,
          label: t("common.entity.group", {
            count: 1,
          }),
          value: selectedGroup.translations?.find((l) => l.languageId === sessionUser.language.id)?.name || "",
        });
      }

      if (selectedProjects.length > 0) {
        items.push({
          icon: <Building04Icon />,
          label: t("common.entity.project", {
            count: selectedProjects.length,
          }),
          value:
            selectedProjects.length > 1
              ? `${selectedProjects[0].name}, ${t("component.audience-selector.ommited-audience.label", {
                  amountOmitted: selectedProjects.length - 1,
                })}`
              : `${selectedProjects[0].name}`,
        });
      }
    }

    return items;
  }, [selectedGroup, selectedProjects, sessionUser.language.id, t]);

  return (
    <>
      <FormErrorWrapper<AdminCreateMassPostFormValues> name="projects">
        <AdminCreatePostFormAudienceSelectorButtonItem
          items={selectedAudienceItems}
          onClick={drawerHandler.setTrue}
          isError={isError}
          isDisabled={!selectedPostType}
          data-testid="audience-selector-btn"
        />
      </FormErrorWrapper>
      <Drawer.Root
        data-testid="audience-selector-drawer"
        title={t("page.admin-create-post.audience-selector.drawer.title")}
        isOpened={isDrawerOpened}
        onOpenChange={(state) => {
          if (!state) {
            form.reset();
          }

          drawerHandler.set(state);
        }}
      >
        <Drawer.Body>
          <Form formMethods={form} onSubmit={() => {}}>
            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-4">
                <h4 className="text-headline4">
                  {t("page.admin-create-mass-message.audience-selector.drawer.subtitle")}.
                </h4>
                <MultiLineContent className="text-body-italic">
                  {t("page.admin-create-mass-message.audience-selector.drawer.description", {
                    amount: selectedProjects.length,
                  })}
                </MultiLineContent>
              </div>
              <FormField label={t("page.create-mass.message.form.group")}>
                <FormSelect<
                  CreateMassPostFormAudienceSelectorFormValues,
                  CreateMassPostFormAudienceSelectorFormValues["selectedGroup"]
                >
                  name="selectedGroup"
                  emptyItem={t("page.create-mass-message.form.group.placeholder")}
                  items={[
                    ...audienceGroups.filter((x) => x.type === "interest"),
                    ...audienceGroups.filter((x) => x.type === "helpCategory"),
                  ]}
                  keySelector={(x) => x.id}
                  renderOption={(x) => (
                    <div className="flex items-center gap-2">
                      <GroupIcon size={16} icon={x.icon} />
                      <span>{x.translations?.find((l) => l.languageId === sessionUser.language.id)?.name}</span>
                    </div>
                  )}
                  groupSelector={(x) => {
                    switch (x.type) {
                      case "interest":
                        return t("page.create-mass-message.form.group.type.interest");
                      case "helpCategory":
                        return t("page.create-mass-message.form.group.type.help-category");
                      default:
                        return "";
                    }
                  }}
                  onChange={(value) => {
                    if (value?.id !== currGroup?.id) {
                      form.setValue("selectedProjects", []);
                    }
                  }}
                  disabled={isFetchingAudienceProjects}
                />
              </FormField>
              <div className="flex flex-col gap-4">
                <FormField label={t("page.create-mass-message.form.projects")} required>
                  <span className="block py-2 text-overline text-grey-700">
                    {t("page.create-mass-message.form.projects.description")}
                  </span>
                  <FormMultiSelect<CreateMassPostFormAudienceSelectorFormValues, "selectedProjects">
                    name="selectedProjects"
                    placeholder={t("page.create-mass-message.form.projects.placeholder")}
                    items={audienceProjects}
                    sortSelectedItems={sortAlphabetically}
                    keySelector={(x) => x.id}
                    renderOption={(x) => x.name}
                    rules={{
                      required: t("components.form.error.required-generic"),
                    }}
                    disabled={isFetchingAudienceProjects || audienceProjects.length === 0}
                  />
                </FormField>
                <div className="flex w-full flex-col gap-2">
                  <Button
                    className="w-full"
                    styling="secondary"
                    onClick={() =>
                      form.setValue("selectedProjects", audienceProjects, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                    data-testid="add-all-projects-btn"
                    disabled={audienceProjects.length === currProjects.length}
                    isLoading={isFetchingAudienceProjects}
                  >
                    {t("page.create-mass-message.form.projects.add-all", {
                      count: audienceProjects.length,
                    })}
                  </Button>
                  <Button
                    className="w-full"
                    styling="danger"
                    onClick={() => form.setValue("selectedProjects", [])}
                    disabled={currProjects.length === 0}
                  >
                    {t("page.create-mass-message.form.projects.clear-all")}
                  </Button>
                </div>
              </div>
            </div>
          </Form>
        </Drawer.Body>

        <Drawer.Actions>
          <Drawer.Close>
            <Button styling="secondary">{t("common.action.cancel")}</Button>
          </Drawer.Close>
          <Button data-testid="confirm-audience-btn" styling="primary" onClick={handleSubmit}>
            {t("common.action.select")}
          </Button>
        </Drawer.Actions>
      </Drawer.Root>
    </>
  );
}
